import React, { useState, useEffect } from 'react';
import { Palette, Sparkles, ArrowRight, Upload, Type, Image } from 'lucide-react';

const Homepage = ({ onNavigateToCreate, onNavigateToUpload, onNavigateToGallery }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const FeatureCard = ({ icon: Icon, title, description, gradient, delay, onClick }) => (
    <div 
      className={`group relative overflow-hidden rounded-2xl p-8 transition-all duration-700 transform hover:scale-105 hover:-translate-y-2 cursor-pointer ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
      style={{ 
        background: `linear-gradient(135deg, ${gradient})`,
        transitionDelay: `${delay}ms`,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}
      onClick={onClick}
    >
      <div className="absolute inset-0 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm group-hover:bg-white/30 transition-all duration-300">
            <Icon size={32} className="text-white" />
          </div>
          <ArrowRight size={24} className="text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">{title}</h3>
        <p className="text-white/90 text-lg leading-relaxed">{description}</p>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/30 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
    </div>
  );

  const handleSignIn = () => {
    console.log('Navigate to sign in');
    // Add authentication logic here
  };

  const handleStartCreating = () => {
    onNavigateToCreate();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-conic from-purple-500/10 via-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-spin" style={{animationDuration: '20s'}}></div>
      </div>

      {/* Mouse Follow Effect */}
      <div 
        className="absolute pointer-events-none"
        style={{
          left: mousePosition.x - 200,
          top: mousePosition.y - 200,
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          transition: 'all 0.1s ease-out'
        }}
      />

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="px-8 py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Palette className="text-purple-400" size={32} />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                canvasGen.ai
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <button 
                className="text-white/80 hover:text-white transition-colors duration-200"
                onClick={onNavigateToGallery}
              >
                Gallery
              </button>
              <button className="text-white/80 hover:text-white transition-colors duration-200">
                About
              </button>
              <button 
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full hover:from-purple-500 hover:to-blue-500 transition-all duration-200 transform hover:scale-105"
                onClick={handleSignIn}
              >
                Sign In
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="px-8 py-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className={`transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <h1 className="text-6xl md:text-8xl font-bold mb-8">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                  Transform
                </span>
                <br />
                <span className="text-white">Your Vision</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed">
                Turn your ideas into stunning artwork with AI-powered painting styles. 
                Create, transform, and bring your imagination to life.
              </p>
            </div>
          </div>
        </div>

        {/* Main Features */}
        <div className="px-8 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center mb-16 transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Choose Your Creative Journey
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Three powerful ways to create stunning artwork with professional painting styles
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <FeatureCard
                icon={Type}
                title="Describe & Create"
                description="Transform your words into breathtaking artwork. Describe your vision, apply artistic styles, then order as canvas, poster, puzzle or digital download."
                gradient="rgba(139, 92, 246, 0.9), rgba(59, 130, 246, 0.8)"
                delay={100}
                onClick={onNavigateToCreate}
              />
              
              <FeatureCard
                icon={Upload}
                title="Upload & Transform"
                description="Upload your images and reimagine them with stunning artistic styles. Turn photos into masterpieces, then order in multiple formats."
                gradient="rgba(236, 72, 153, 0.9), rgba(139, 92, 246, 0.8)"
                delay={300}
                onClick={onNavigateToUpload}
              />
              
              <FeatureCard
                icon={Image}
                title="Browse & Order"
                description="Explore our curated collection of AI-generated artwork. Find inspiration and order prints, canvases, puzzles or downloads instantly."
                gradient="rgba(59, 130, 246, 0.9), rgba(16, 185, 129, 0.8)"
                delay={500}
                onClick={onNavigateToGallery}
              />
            </div>
          </div>
        </div>

        {/* Order Options Section */}
        <div className="px-8 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center mb-16 transition-all duration-1000 delay-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <h2 className="text-4xl font-bold text-white mb-6">Bring Your Art to Life</h2>
              <p className="text-lg text-white/70 max-w-3xl mx-auto">
                Once you've created or selected your perfect artwork, order it in multiple formats to enjoy and share
              </p>
            </div>

            <div className={`grid md:grid-cols-4 gap-6 mb-20 transition-all duration-1000 delay-800 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              {[
                { icon: '🖼️', title: 'Digital Download', desc: 'High-res image file' },
                { icon: '📄', title: 'Premium Poster', desc: 'Museum-quality prints' },
                { icon: '🎨', title: 'Canvas Painting', desc: 'Gallery-wrapped canvas' },
                { icon: '🧩', title: 'Custom Puzzle', desc: '500-1000 piece puzzles' }
              ].map((item, index) => (
                <div 
                  key={item.title}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Painting Styles Preview */}
        <div className="px-8 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center mb-16 transition-all duration-1000 delay-900 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <h2 className="text-4xl font-bold text-white mb-6">Artistic Styles Available</h2>
              <p className="text-lg text-white/70">Choose from professional painting techniques</p>
            </div>

            <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 transition-all duration-1000 delay-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              {['Impressionist', 'Realism', 'Watercolor', 'Cyberpunk', 'Oil Canvas', 'Charcoal', 'Surrealism', 'Pop Art', 'Art Nouveau', 'Cartoon', 'Custom', '+ More'].map((style, index) => (
                <div 
                  key={style}
                  className="p-4 bg-white/10 backdrop-blur-sm rounded-xl text-center text-white hover:bg-white/20 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className="text-sm font-medium">{style}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="px-8 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-3xl p-12 border border-white/10 transition-all duration-1000 delay-1100 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
              <Sparkles className="mx-auto mb-6 text-purple-400" size={48} />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Create & Order?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                Create stunning artwork and bring it into the physical world with our premium printing options
              </p>
              <button 
                className="px-12 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg font-semibold rounded-full hover:from-purple-500 hover:to-blue-500 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={handleStartCreating}
              >
                Start Creating Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;